import React, { ReactNode, useEffect, useState } from 'react'
import { useConfigurationStore, useCountdownFSBonusStore } from '@stores'
import { useFormatMoney } from '@hooks'
import { getCountdownDisplayItems, getCountdownValuesFromBonus } from '@utils/bonus'
import spritesheetSrc from '@assets/bonus-system/countdown-gift-animation.png'
import animatedSpriteConfig from '@assets/bonus-system/countdown-gift-animation.json'
import { useTranslation } from '@amusnet/translations/react'
import { CountdownCounterDisplayItems, CountdownCounterValues } from '../types'
import { CountdownStrategy } from '@amusnet/bonuses-http-client'
import { AnimatedSprite, CountdownTimer } from '@amusnet/react-ui'
import { CountdownFreeSpinsBonus } from '@amusnet/mgs-ws-client'

interface CFSSingleBonusProps {
    updateSelectedBonus: (bonus: CountdownFreeSpinsBonus) => void
}

const CFSSingleBonus: React.FC<CFSSingleBonusProps> = ({ updateSelectedBonus }: CFSSingleBonusProps) => {
    const bonuses = useCountdownFSBonusStore((state) => state.bonuses)

    const { t } = useTranslation()
    const formatMoney = useFormatMoney()
    const currency = useConfigurationStore((state) => state.currency)

    const [betValue, setBetValue] = useState('')
    const [bonus, setBonus] = useState<CountdownFreeSpinsBonus>()

    useEffect(() => {
        if (bonuses.length) {
            const currentBonus = bonuses[0]

            setBonus(currentBonus)
            updateSelectedBonus(currentBonus)
            setBetValue(formatMoney(Number(currentBonus.betValue)))
        }
    }, [bonuses, formatMoney])

    const renderCounter = () => {
        let counterValue: ReactNode

        switch (bonus?.typeConfiguration?.countdownFreeSpinConfiguration?.countdownStrategy) {
            case CountdownStrategy.RELATIVE_TO_PLAY_NOW: {
                const countdown: CountdownCounterValues = getCountdownValuesFromBonus(bonus, true)

                if (!countdown) return null

                const displayItems: CountdownCounterDisplayItems = [
                    { val: countdown.days, label: t('common.bonusSystem.timeLeftDays') },
                    { val: countdown.hours, label: t('common.bonusSystem.timeLeftHours') },
                    { val: countdown.minutes, label: t('common.bonusSystem.timeLeftMinutes') },
                    { val: countdown.seconds, label: t('common.bonusSystem.timeLeftSeconds') },
                ]

                const { firstStringDigits, firstStringLabel, secondStringDigits, secondStringLabel } =
                    getCountdownDisplayItems(displayItems)

                counterValue = (
                    <>
                        <span
                            id="cfs-single-bonus-counter-label"
                            className="text-4xl text-[#D9D9D9] leading-snug"
                        >{`${t('gameui.bonusSystem.forText')}: `}</span>
                        <span
                            id="cfs-single-bonus-counter-value"
                            className="text-4xl font-black text-yellow-300 leading-snug"
                        >
                            <span id="cfs-single-bonus-counter-first-digits" className="font-black">
                                {firstStringDigits}
                            </span>
                            <span id="cfs-single-bonus-counter-first-label">{firstStringLabel}</span>
                            <span id="cfs-single-bonus-counter-second-digits" className="font-black">
                                {secondStringDigits}
                            </span>
                            <span id="cfs-single-bonus-counter-second-label">{secondStringLabel}</span>
                        </span>
                    </>
                )
                break
            }
            case CountdownStrategy.CUSTOM: {
                const countdownDateTime = bonus.typeConfiguration?.countdownFreeSpinConfiguration?.countdownDateTime

                if (!countdownDateTime) return null

                counterValue = (
                    <>
                        <span
                            id="cfs-single-bonus-counter-label"
                            className="text-4xl text-[#D9D9D9] leading-snug"
                        >{`${t('common.promo.endsIn_capitalised')}: `}</span>
                        <span
                            id="cfs-single-bonus-counter-value"
                            className="text-4xl font-black text-yellow-300 leading-snug"
                        >
                            <span>
                                <CountdownTimer until={new Date(countdownDateTime)} className={'text-yellow-300'} />
                            </span>
                        </span>
                    </>
                )
                break
            }
            // PROMOTION_END_DATE is only handled by BE and is sent as CUSTOM instead.
        }

        return (
            <p id="cfs-single-bonus-counter" className="rounded-[30px] font-bold bg-[#1E1B1B] py-[8px] px-[25px]">
                {counterValue}
            </p>
        )
    }

    return (
        <div id="cfs-single-bonus" className="flex flex-col items-center justify-between">
            <div id="cfs-single-bonus-content" className="flex flex-col w-full bg-[#19191C]">
                <AnimatedSprite
                    spritesheetSrc={spritesheetSrc}
                    config={animatedSpriteConfig}
                    duration={1.5}
                    className="m-auto"
                />

                <div
                    id="countdown-freespin-bonus-text"
                    className="flex flex-col items-center w-full text-center pb-4 bg-[linear-gradient(180deg,#19191C_19.44%,#793F06_100%)]"
                >
                    <p id="cfs-single-bonus-congratulations" className="text-4xl font-bold text-white">
                        {t('gameui.congratulations')}
                    </p>
                    <p id="cfs-single-bonus-you-get" className="text-4xl font-bold text-white my-[10px]">
                        {t('gameui.bonusSystem.youGet')}
                    </p>
                    <p id="cfs-single-bonus-title" className="text-4xl font-bold text-yellow-300 leading-tight px-1">
                        {t('gameui.bonusSystem.unlimitedFreeSpinsTitle')}
                    </p>
                    <p id="cfs-single-bonus-bet-amount" className="text-4xl font-black text-white my-[10px]">
                        x {betValue}{' '}
                        <span id="cfs-single-bonus-bet-currency" className="text-[24px] font-normal">
                            {currency}
                        </span>
                    </p>
                    {renderCounter()}
                </div>
            </div>
        </div>
    )
}

export default CFSSingleBonus
